import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Tooltip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  ImageOutlined as ImageIcon,
  CheckCircle as CheckCircleIcon,
  EventBusy as EventBusyIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import api from '../api/api';
import dayjs from 'dayjs';

// ── Colores consistentes con el resto del proyecto ──────────────
const PRIMARY_COLOR   = '#4f46e5';
const SUCCESS_COLOR   = '#10b981';
const WARNING_COLOR   = '#f59e0b';
const DANGER_COLOR    = '#ef4444';
const SECONDARY_COLOR = '#6b7280';
const PRIMARY_DARK_BG = '#0f172a';
const MAX_SIZE_MB      = 2;
const ALLOWED_TYPES    = ['image/png', 'image/jpeg', 'image/webp'];

// ── Helper: estado de vigencia del banner ────────────────────────
const getVigencia = (banner) => {
  const hoy       = dayjs();
  const inicio    = dayjs(banner.fecha_inicio);
  const fin       = dayjs(banner.fecha_fin);
  if (!banner.activo) return { label: 'Inactivo',  color: SECONDARY_COLOR, icon: <ToggleOffIcon fontSize="small" /> };
  if (hoy.isBefore(inicio))  return { label: 'Programado', color: WARNING_COLOR,  icon: <ScheduleIcon    fontSize="small" /> };
  if (hoy.isAfter(fin))      return { label: 'Vencido',    color: DANGER_COLOR,   icon: <EventBusyIcon   fontSize="small" /> };
  return { label: 'Vigente', color: SUCCESS_COLOR, icon: <CheckCircleIcon fontSize="small" /> };
};

// ════════════════════════════════════════════════════════════════
//  MODAL: Subir nuevo banner
// ════════════════════════════════════════════════════════════════
const NuevoBannerModal = ({ open, onClose, onSuccess }) => {
  const inputRef = useRef(null);
  const [form, setForm]       = useState({ nombre_banner: '', fecha_inicio: '', fecha_fin: '' });
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]     = useState('');

  // Limpiar al abrir
  useEffect(() => {
    if (open) {
      setForm({ nombre_banner: '', fecha_inicio: '', fecha_fin: '' });
      setArchivo(null);
      setPreview(null);
      setError('');
    }
  }, [open]);

  const handleArchivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Solo se aceptan imágenes PNG, JPG o WebP.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`La imagen no debe superar ${MAX_SIZE_MB} MB.`);
      return;
    }
    setError('');
    setArchivo(file);
    setPreview(URL.createObjectURL(file));
  };

  const isValid =
    form.nombre_banner.trim() &&
    form.fecha_inicio &&
    form.fecha_fin &&
    form.fecha_fin >= form.fecha_inicio &&
    archivo;

  const handleGuardar = async () => {
    if (!isValid) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('nombre_banner', form.nombre_banner.trim());
      fd.append('fecha_inicio',  form.fecha_inicio);
      fd.append('fecha_fin',     form.fecha_fin);
      fd.append('imagen',        archivo);

      await api.post('/banners/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSuccess('Banner creado correctamente', 'success');
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el banner.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700, color: PRIMARY_DARK_BG }}>
        Nuevo Banner Promocional
      </DialogTitle>

      <DialogContent dividers>
        {/* Zona de drop / click para seleccionar imagen */}
        <Box
          onClick={() => inputRef.current?.click()}
          sx={{
            border: `2px dashed ${preview ? PRIMARY_COLOR : '#cbd5e1'}`,
            borderRadius: 3,
            p: 3,
            mb: 2,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: preview ? '#eef2ff' : '#f8fafc',
            transition: 'all 0.2s',
            '&:hover': { borderColor: PRIMARY_COLOR, bgcolor: '#eef2ff' },
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={handleArchivo}
          />
          {preview ? (
            <Box>
              <img
                src={preview}
                alt="preview"
                style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 8, objectFit: 'contain' }}
              />
              <Typography variant="caption" display="block" sx={{ mt: 1, color: SECONDARY_COLOR }}>
                {archivo?.name} · {(archivo?.size / 1024).toFixed(0)} KB
              </Typography>
              <Typography variant="caption" sx={{ color: PRIMARY_COLOR }}>
                Haz clic para cambiar la imagen
              </Typography>
            </Box>
          ) : (
            <Box>
              <ImageIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
              <Typography variant="body2" sx={{ color: SECONDARY_COLOR }}>
                Haz clic para seleccionar una imagen
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                PNG, JPG o WebP · máx. {MAX_SIZE_MB} MB
              </Typography>
            </Box>
          )}
        </Box>

        {/* Campos del formulario */}
        <TextField
          label="Nombre del banner"
          fullWidth
          margin="dense"
          placeholder="Ej: Navidad 2025, Semana Santa..."
          value={form.nombre_banner}
          onChange={(e) => setForm({ ...form, nombre_banner: e.target.value })}
          error={!form.nombre_banner.trim()}
          helperText={!form.nombre_banner.trim() ? 'Campo requerido' : ''}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <TextField
            label="Fecha inicio"
            type="date"
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={form.fecha_inicio}
            onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
            error={!form.fecha_inicio}
          />
          <TextField
            label="Fecha fin"
            type="date"
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={form.fecha_fin}
            onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
            error={!form.fecha_fin || form.fecha_fin < form.fecha_inicio}
            helperText={form.fecha_fin && form.fecha_fin < form.fecha_inicio ? 'Debe ser ≥ fecha inicio' : ''}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        {uploading && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={uploading}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          onClick={handleGuardar}
          disabled={!isValid || uploading}
          sx={{ bgcolor: PRIMARY_COLOR }}
        >
          {uploading ? 'Subiendo...' : 'Guardar Banner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL: Gestión de Banners
// ════════════════════════════════════════════════════════════════
const BannerPromocional = () => {
  const [banners, setBanners]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [openModal, setOpenModal]   = useState(false);
  const [deleting, setDeleting]     = useState(null);   // id del que se está borrando
  const [toggling, setToggling]     = useState(null);   // id del que se está toggling
  const [confirmDelete, setConfirmDelete] = useState(null); // banner a confirmar borrado
  const [snackbar, setSnackbar]     = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ── Cargar lista ────────────────────────────────────────────
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const res = await api.get('/banners/');
      setBanners(res.data);
    } catch (err) {
      showSnackbar('Error al cargar los banners', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBanners(); }, []);

  // ── Toggle activo/inactivo ───────────────────────────────────
  const handleToggle = async (banner) => {
    setToggling(banner.id_banner);
    try {
      await api.patch(`/banners/${banner.id_banner}/toggle`);
      showSnackbar(
        banner.activo ? 'Banner desactivado' : 'Banner activado',
        banner.activo ? 'warning' : 'success'
      );
      fetchBanners();
    } catch (err) {
      showSnackbar('Error al cambiar el estado', 'error');
    } finally {
      setToggling(null);
    }
  };

  // ── Eliminar ────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(confirmDelete.id_banner);
    try {
      await api.delete(`/banners/${confirmDelete.id_banner}`);
      showSnackbar('Banner eliminado correctamente');
      setConfirmDelete(null);
      fetchBanners();
    } catch (err) {
      showSnackbar('Error al eliminar el banner', 'error');
    } finally {
      setDeleting(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <Box>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY_DARK_BG }}>
            Banners Promocionales
          </Typography>
          <Typography variant="body2" sx={{ color: SECONDARY_COLOR, mt: 0.5 }}>
            Las imágenes vigentes aparecerán automáticamente en las recetas PDF generadas.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => setOpenModal(true)}
          sx={{ bgcolor: PRIMARY_COLOR }}
        >
          Subir Banner
        </Button>
      </Box>

      {/* Tabla */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : banners.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <ImageIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" sx={{ color: SECONDARY_COLOR }}>
            No hay banners registrados
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
            Sube tu primer banner para Navidad, Semana Santa, vacaciones u otras promociones.
          </Typography>
          <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setOpenModal(true)} sx={{ bgcolor: PRIMARY_COLOR }}>
            Subir primer banner
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Vista previa</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {banners.map((banner) => {
                const vigencia = getVigencia(banner);
                return (
                  <TableRow key={banner.id_banner} hover>
                    {/* Vista previa: llama a /static/banners/{nombre_archivo} */}
                    <TableCell>
                      <Box
                        component="img"
                        src={`${api.defaults.baseURL?.replace('/api', '')}/static/banners/${banner.ruta_imagen}`}
                        alt={banner.nombre_banner}
                        onError={(e) => { e.target.style.display = 'none'; }}
                        sx={{
                          height: 48,
                          maxWidth: 120,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          bgcolor: '#f8fafc',
                          p: 0.5,
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{banner.nombre_banner}</Typography>
                      <Typography variant="caption" sx={{ color: SECONDARY_COLOR }}>{banner.ruta_imagen}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {dayjs(banner.fecha_inicio).format('DD/MM/YYYY')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: SECONDARY_COLOR }}>
                        hasta {dayjs(banner.fecha_fin).format('DD/MM/YYYY')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={vigencia.icon}
                        label={vigencia.label}
                        size="small"
                        sx={{
                          bgcolor: `${vigencia.color}18`,
                          color: vigencia.color,
                          fontWeight: 700,
                          border: `1px solid ${vigencia.color}40`,
                          '& .MuiChip-icon': { color: vigencia.color },
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {/* Toggle activo */}
                        <Tooltip title={banner.activo ? 'Desactivar' : 'Activar'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleToggle(banner)}
                              disabled={toggling === banner.id_banner}
                              sx={{ color: banner.activo ? SUCCESS_COLOR : SECONDARY_COLOR }}
                            >
                              {toggling === banner.id_banner
                                ? <CircularProgress size={18} />
                                : banner.activo ? <ToggleOnIcon /> : <ToggleOffIcon />
                              }
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Eliminar */}
                        <Tooltip title="Eliminar banner">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setConfirmDelete(banner)}
                              disabled={deleting === banner.id_banner}
                              sx={{ color: DANGER_COLOR }}
                            >
                              {deleting === banner.id_banner
                                ? <CircularProgress size={18} />
                                : <DeleteIcon />
                              }
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Modal: Nuevo Banner ─────────────────────────────────── */}
      <NuevoBannerModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSuccess={(msg, sev) => { showSnackbar(msg, sev); fetchBanners(); }}
      />

      {/* ── Diálogo: Confirmar eliminación ──────────────────────── */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: DANGER_COLOR, fontWeight: 700 }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar el banner <strong>{confirmDelete?.nombre_banner}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: SECONDARY_COLOR, mt: 1 }}>
            Se eliminará el archivo del servidor. Esta acción es irreversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={!!deleting}
            sx={{ bgcolor: DANGER_COLOR }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BannerPromocional;
